import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";

export default function UsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "gerant" });
  const [mode, setMode] = useState("invite");

  const load = async () => setUsers((await api.get("/users")).data);
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      if (mode === "invite") {
        await api.post("/users/invite", { name: form.name, email: form.email, role: form.role });
        toast.success(`Invitation envoyée à ${form.email}`);
      } else {
        await api.post("/users", form);
        toast.success("Utilisateur créé");
      }
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "gerant" });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const reinvite = async (u) => {
    try { await api.post(`/users/${u.id}/reinvite`); toast.success(`Invitation renvoyée à ${u.email}`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const del = async (id) => { await api.delete(`/users/${id}`); load(); };

  const togglePerm = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { peut_supprimer_verrouille: !u.peut_supprimer_verrouille });
      load();
      toast.success(u.peut_supprimer_verrouille ? "Droit retiré" : "Droit accordé");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-dd-blue mb-1">Utilisateurs</h1>
          <p className="text-sm text-dd-warm">Invitez vos gérants par e-mail : ils choisissent eux-mêmes leur mot de passe.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="new-user-btn" className="h-11 bg-dd-blue hover:bg-dd-night text-white">
          <Plus size={16} className="mr-1" /> Nouvel utilisateur
        </Button>
      </div>

      <div className="bg-white rounded-lg border border-dd-light overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="bg-dd-blue text-white text-left">
            <th className="px-4 py-3">Nom</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Rôle</th><th className="px-4 py-3">Suppr. constats signés</th><th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-dd-light" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 text-dd-night font-medium">{u.name}</td>
                <td className="px-4 py-3 text-dd-night">{u.email}</td>
                <td className="px-4 py-3"><Badge className="bg-dd-light text-dd-night border-0">{u.role === "admin" ? "Administrateur" : "Gérant"}</Badge>
                  {u.invite_pending && <Badge className="ml-2 bg-dd-blue/10 text-dd-blue border-0" data-testid={`invite-pending-${u.id}`}>Invitation en attente</Badge>}</td>
                <td className="px-4 py-3">
                  {u.role === "admin" ? <span className="text-xs text-dd-warm">Toujours</span>
                    : <Switch checked={!!u.peut_supprimer_verrouille} onCheckedChange={() => togglePerm(u)} data-testid={`perm-suppr-${u.id}`} />}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {u.invite_pending && <button onClick={() => reinvite(u)} className="text-dd-blue h-10 px-2 text-xs font-medium" data-testid={`reinvite-${u.id}`}>Renvoyer</button>}
                  {u.id !== user.id && <button onClick={() => del(u.id)} className="text-destructive h-10 w-10 inline-flex items-center justify-center" data-testid={`delete-user-${u.id}`}><Trash2 size={16} /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-dd-night/50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-dd-blue mb-4">{mode === "invite" ? "Inviter un utilisateur" : "Nouvel utilisateur"}</h3>
            <div className="flex gap-1 mb-4 rounded-md bg-dd-light/50 p-1">
              {[["invite", "Inviter par e-mail"], ["manual", "Définir le mot de passe"]].map(([m, l]) => (
                <button key={m} onClick={() => setMode(m)} data-testid={`user-mode-${m}`}
                  className={`flex-1 h-10 rounded text-sm font-medium ${mode === m ? "bg-white text-dd-blue" : "text-dd-warm"}`}>{l}</button>
              ))}
            </div>
            <div className="space-y-3">
              <div><Label className="text-dd-night">Nom</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 mt-1" data-testid="user-name" /></div>
              <div><Label className="text-dd-night">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 mt-1" data-testid="user-email" /></div>
              {mode === "manual" ? (
                <div><Label className="text-dd-night">Mot de passe</Label><Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-11 mt-1" data-testid="user-password" /></div>
              ) : <p className="text-xs text-dd-warm">Un e-mail aux couleurs de la gérance sera envoyé avec un lien (72 h) pour choisir le mot de passe.</p>}
              <div>
                <Label className="text-dd-night">Rôle</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="h-11 mt-1" data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gerant">Gérant</SelectItem>
                    <SelectItem value="admin">Administrateur</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setOpen(false)} className="h-11 border-dd-light">Annuler</Button>
              <Button onClick={create} data-testid="user-save" className="h-11 bg-dd-blue hover:bg-dd-night text-white">Créer</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
